import { useEffect, useRef } from 'react';

/**
 * MouseSpotlight — halo terracotta qui suit le curseur. Ambient warmth wellness.
 * Port EGSF adapté Océanne (theme cream warm vs EGSF dark navy).
 *
 * Desktop uniquement (skip si pointer:coarse = touch device).
 * Skip aussi si prefers-reduced-motion (a11y).
 *
 * z-index 0 pour rester en arrière-plan (sous tout sauf le body bg).
 * mix-blend-mode: multiply — réchauffe subtilement le cream sous le curseur.
 * (EGSF utilise `screen` qui marche sur dark, mais devient invisible sur cream.)
 *
 * Alpha 14%/6% (vs EGSF 7%/3%) car multiply nécessite plus de saturation
 * pour rester visible sur surface cream wellness.
 */
export function MouseSpotlight() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = ref.current;
    if (!el) return;

    let rafId = 0;
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let currentX = targetX;
    let currentY = targetY;

    const onMouseMove = (e: MouseEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;
      if (!rafId) rafId = requestAnimationFrame(animate);
    };

    const animate = () => {
      // Lerp 0.08 pour un suivi smooth (pas d'à-coups) — même valeur qu'EGSF
      currentX += (targetX - currentX) * 0.08;
      currentY += (targetY - currentY) * 0.08;
      el.style.transform = `translate(${currentX - 250}px, ${currentY - 250}px)`;

      if (Math.abs(targetX - currentX) > 0.5 || Math.abs(targetY - currentY) > 0.5) {
        rafId = requestAnimationFrame(animate);
      } else {
        rafId = 0;
      }
    };

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="fixed top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none z-0"
      style={{
        background:
          'radial-gradient(circle, color-mix(in oklch, var(--primary) 7%, transparent) 0%, color-mix(in oklch, var(--primary) 3%, transparent) 30%, transparent 70%)',
        filter: 'blur(60px)',
        willChange: 'transform',
        mixBlendMode: 'multiply'
      }}
    />
  );
}
